import { apiClient, type ApiClientResponse } from '@/datasources/apiClient'
import type { ApiKey } from '@/types'

export interface LoginResult {
  user: {
    id: string
    email: string
    displayName: string
    avatarUrl?: string
    roles: string[]
    createdAt: string
  }
  accessToken: string
  refreshToken: string
  isNewUser: boolean
}

export type { ApiKey }

export interface ConsentRequiredData {
  email: string
  oauthProvider?: string
}

export type LoginApiResponse =
  | { code: 2000 | 2001; data: LoginResult; messages: string[] }
  | { code: 2002; data: ConsentRequiredData; messages: string[] }

export interface LoginWithCodeParams {
  email: string
  code: string
  fullName?: string
  consent?: boolean
}

export const sendOtpApi = (email: string): Promise<ApiClientResponse<null>> =>
  apiClient
    .post('/api/v1/accounts/mailer', { email, locale: localStorage.getItem('locale') || 'enUS' })
    .then((r) => r.data)

export const loginApi = (params: LoginWithCodeParams): Promise<LoginApiResponse> =>
  apiClient
    .post('/api/v1/auth', {
      mode: 'code',
      email: params.email,
      code: params.code,
      fullName: params.fullName,
      consent: params.consent,
    })
    .then((r) => r.data as LoginApiResponse)

export type RefreshResult = {
  user: LoginResult['user']
  accessToken: string
  refreshToken: string
}

export const refreshApi = (refreshToken: string): Promise<ApiClientResponse<RefreshResult>> =>
  apiClient.post('/api/v1/auth/refresh', { refreshToken }).then((r) => r.data)

export const fetchMeApi = (): Promise<ApiClientResponse<LoginResult['user']>> =>
  apiClient.get('/api/v1/accounts/me').then((r) => r.data)

export const patchMeApi = (data: {
  displayName?: string
  avatarUrl?: string
}): Promise<ApiClientResponse<LoginResult['user']>> =>
  apiClient.patch('/api/v1/accounts/me', data).then((r) => r.data)



export const listApiKeysApi = (): Promise<ApiClientResponse<ApiKey[]>> =>
  apiClient.get('/api/v1/auth/api-keys').then((r) => r.data)

export const createApiKeyApi = (
  name: string,
  orgId: string,
  source?: 'login' | 'manual',
): Promise<ApiClientResponse<ApiKey>> =>
  apiClient.post('/api/v1/auth/api-keys', { name, orgId, source }).then((r) => r.data)

export const revokeApiKeyApi = (id: string): Promise<ApiClientResponse<null>> =>
  apiClient.delete(`/api/v1/auth/api-keys/${id}`).then((r) => r.data)

export const rotateApiKeyApi = (id: string): Promise<ApiClientResponse<ApiKey>> =>
  apiClient.post(`/api/v1/auth/api-keys/${id}/rotate`).then((r) => r.data)
